// Local-first "My creations" store. Cards a shopper designs are saved to localStorage so they can
// find them again on the same browser with no account. Email recovery (Part 4) layers a server link
// on top for cross-device access; this module also tracks whether that link has been collected so the
// email prompt can hide itself.
//
// All access is wrapped in try/catch: localStorage can throw (private mode, quota, SSR) and a failed
// save must never break the create flow.

const CREATIONS_KEY = 'myCreations';
const SESSION_KEY = 'creationSessionId';
const RECOVERY_FLAG_KEY = 'creationRecoveryToken';

const safeGet = (key) => {
  try {
    return typeof localStorage !== 'undefined' ? localStorage.getItem(key) : null;
  } catch {
    return null;
  }
};

const safeSet = (key, value) => {
  try {
    if (typeof localStorage !== 'undefined') localStorage.setItem(key, value);
  } catch {
    /* ignore */
  }
};

// A stable, anonymous id for this browser, used to associate creations with an email on recovery.
export const getSessionId = () => {
  let id = safeGet(SESSION_KEY);
  if (!id) {
    id =
      typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : `sess-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    safeSet(SESSION_KEY, id);
  }
  return id;
};

// Newest first.
export const getCreations = () => {
  const raw = safeGet(CREATIONS_KEY);
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

// Append a freshly-created card. Invisible: no UI, no "saved on this computer" copy. Dedupes by
// cardId (re-saving the same card moves it to the front with updated data).
export const addCreation = (creation) => {
  if (!creation || !creation.cardId) return getCreations();
  const record = {
    cardId: creation.cardId,
    coverImageUrl: creation.coverImageUrl || null,
    coverImageId: creation.coverImageId || null,
    createdAt: creation.createdAt || new Date().toISOString(),
    inputs: creation.inputs || null,
  };
  const existing = getCreations().filter((c) => c.cardId !== record.cardId);
  const next = [record, ...existing];
  safeSet(CREATIONS_KEY, JSON.stringify(next));
  return next;
};

// Self-heal an old creation IN PLACE with a resolved stable cover (My-creations SELF-HEAL). Entries
// created before the stable-hosting fix persisted an expiring URL and NO coverImageId; once we resolve
// the card's cover by id we patch just that entry — order preserved, no dedupe/reorder. Idempotent:
// only fields provided are updated, and callers skip entries that already have a coverImageId.
export const updateCreationCover = (cardId, { coverImageId, coverImageUrl } = {}) => {
  if (!cardId) return getCreations();
  const all = getCreations();
  let changed = false;
  const next = all.map((c) => {
    if (c.cardId !== cardId) return c;
    changed = true;
    return {
      ...c,
      coverImageId: coverImageId || c.coverImageId || null,
      // A freshly-resolved stable URL replaces the stale/expiring one; keep the old only if none came back.
      coverImageUrl: coverImageUrl || c.coverImageUrl || null,
    };
  });
  if (changed) safeSet(CREATIONS_KEY, JSON.stringify(next));
  return next;
};

// --- Browse-away pending creations (Phase B) --------------------------------------------------------
// When a generation is kicked off, we record a PENDING placeholder keyed by generationId so the user can
// leave the wait screen and still find the design landing in "My creations". The Creations page polls
// each pending item and resolves it to a real card when the background job finishes. Deduped by
// generationId; a pending item has NO cardId until it resolves.

export const addPendingCreation = (pending) => {
  if (!pending || !pending.generationId) return getCreations();
  const record = {
    generationId: pending.generationId,
    status: 'creating',
    coverImageUrl: null,
    createdAt: pending.createdAt || new Date().toISOString(),
    inputs: pending.inputs || null,
  };
  const existing = getCreations().filter((c) => c.generationId !== record.generationId);
  const next = [record, ...existing];
  safeSet(CREATIONS_KEY, JSON.stringify(next));
  return next;
};

// Replace a resolved pending placeholder with the finished card (cardId set, status cleared). No-op if
// the placeholder is gone (e.g. the user already finalized it from the wait screen).
export const resolvePendingCreation = (generationId, done) => {
  if (!generationId) return getCreations();
  const all = getCreations();
  if (!all.some((c) => c.generationId === generationId)) return all;
  const prior = all.find((c) => c.generationId === generationId) || {};
  const record = {
    cardId: (done && done.cardId) || null,
    coverImageUrl: (done && done.coverImageUrl) || null,
    coverImageId: (done && done.coverImageId) || null,
    createdAt: prior.createdAt || new Date().toISOString(),
    inputs: prior.inputs || null,
  };
  const rest = all.filter((c) => c.generationId !== generationId && c.cardId !== record.cardId);
  const next = [record, ...rest];
  safeSet(CREATIONS_KEY, JSON.stringify(next));
  return next;
};

// Drop a pending placeholder (the job failed / was abandoned) so a dead "creating…" tile never lingers.
export const removePendingCreation = (generationId) => {
  if (!generationId) return getCreations();
  const next = getCreations().filter((c) => c.generationId !== generationId);
  safeSet(CREATIONS_KEY, JSON.stringify(next));
  return next;
};

// True once an email/recovery link has been collected — used to hide the email prompt thereafter.
export const hasRecoveryToken = () => !!safeGet(RECOVERY_FLAG_KEY);

export const setRecoveryToken = (token) => {
  safeSet(RECOVERY_FLAG_KEY, token || '1');
};
